import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import * as zod from "zod";
import { Importer as Importer$1 } from "react-csv-importer";
import { ImporterField } from "react-csv-importer";
import { Card, Alert, ScrollShadow } from "@heroui/react";
import { useCallback, useImperativeHandle } from "react";
import { withModalController, useModalController } from "@livefree/react-ui";
import { omit } from "@livefree/common";
import { ReactiveCore, DexieDatabaseProvider } from "@livefree/reactive";
const CsvImporter = ({ validator, dataHandler, processChunk: _processChunk, ...rest }) => {
  const validate = validator ? validator(zod) : void 0;
  let data = [];
  const handleDataHandler = async (rows, info) => {
    if (validate) {
      data = rows.map((row) => {
        const result = validate.safeParse(row);
        return result.success ? {
          valid: 1,
          messages: [],
          data: result.data
        } : {
          valid: 0,
          messages: result.error.issues.map((issue) => ({
            path: issue.path,
            message: issue.message
          })),
          data: row
        };
      });
    } else {
      data = rows.map((row) => ({
        valid: 1,
        messages: [],
        data: row
      }));
    }
    return await Promise.resolve(dataHandler?.(data, info));
  };
  return /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsx(Card.Content, { children: /* @__PURE__ */ jsx(Importer$1, { ...rest, dataHandler: handleDataHandler }) }) });
};
const reactive = new ReactiveCore("csv");
class Importer {
  clear() {
    return Promise.resolve();
  }
  save(records) {
    return Promise.resolve(records);
  }
}
const ImportService = reactive.service("import", Importer)((Service) => new Service());
ImportService.reactor("importReactor", {
  success: (response, services, db) => {
    const { source, payload } = response;
    switch (source) {
      case ImportService.clear:
        return db.records.clear();
      case ImportService.save:
        return db.records.bulkPut(payload);
    }
  }
});
const ReactiveConfig = {
  db: {
    name: "livefree.csv.db",
    provider: new DexieDatabaseProvider(),
    init: (db) => {
      db.version(1).stores({
        records: "++id,valid"
      });
    }
  }
};
const withReactive = (Component) => {
  const Wrapped = (props) => {
    const Rendered = reactive.configure(ReactiveConfig).render(() => /* @__PURE__ */ jsx(Component, { ...props }));
    return /* @__PURE__ */ jsx(Rendered, {});
  };
  return Wrapped;
};
const toPathArray = (path) => {
  if (Array.isArray(path)) return path.map((s) => String(s));
  if (typeof path === "string") return path ? path.split(".").filter(Boolean) : [];
  return [];
};
const pathMatches = (msgPath, currentPath) => {
  const a = toPathArray(msgPath);
  const b = currentPath.map((s) => String(s));
  return a.length === b.length && a.every((seg, i) => seg === b[i]);
};
const highlightClass = "rounded-sm px-1 font-medium bg-warning/25 text-warning ring-2 ring-warning/70 dark:bg-warning/30 dark:text-warning dark:ring-warning/80";
const messageClass = "block rounded-sm my-2 px-3 py-2 text-sm bg-warning/20 text-warning ring-1 ring-warning/60 dark:bg-warning/25 dark:text-warning dark:ring-warning/70";
const DataTree = ({ data, messages = [], basePath = [] }) => {
  if (data === null || data === void 0) {
    const msgsForPath2 = messages.filter((m) => pathMatches(m.path, basePath));
    return /* @__PURE__ */ jsxs("span", { className: "text-default-500 italic", children: [
      msgsForPath2.length > 0 && /* @__PURE__ */ jsx("span", { className: messageClass, children: msgsForPath2.map((m, i) => /* @__PURE__ */ jsx("span", { className: "my-2", children: m.message }, i)) }),
      String(data)
    ] });
  }
  if (typeof data === "object" && !Array.isArray(data) && data !== null) {
    const entries = Object.entries(data);
    return /* @__PURE__ */ jsx("div", { className: "ml-2 pl-2 border-l border-default-200 dark:border-default-100", children: entries.map(([key, value]) => {
      const currentPath = [...basePath, key];
      const msgsForPath2 = messages.filter((m) => pathMatches(m.path, currentPath));
      const isHighlighted = msgsForPath2.length > 0;
      return /* @__PURE__ */ jsx("div", { className: "my-1", children: /* @__PURE__ */ jsxs("span", { children: [
        /* @__PURE__ */ jsxs("span", { className: isHighlighted ? highlightClass : "text-default-600 dark:text-default-400", children: [
          key,
          ":"
        ] }),
        " ",
        typeof value === "object" && value !== null && !Array.isArray(value) ? /* @__PURE__ */ jsx(DataTree, { data: value, messages, basePath: currentPath }) : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("span", { className: isHighlighted ? highlightClass : "", children: typeof value === "string" ? `"${value}"` : String(value) }),
          msgsForPath2.length > 0 && /* @__PURE__ */ jsx("span", { className: messageClass, children: msgsForPath2.map((m, i) => /* @__PURE__ */ jsx("span", { children: m.message }, i)) })
        ] })
      ] }) }, key);
    }) });
  }
  if (Array.isArray(data)) {
    return /* @__PURE__ */ jsxs("span", { children: [
      "[",
      data.map((item, i) => /* @__PURE__ */ jsxs("span", { children: [
        i > 0 && ", ",
        /* @__PURE__ */ jsx(DataTree, { data: item, messages, basePath: [...basePath, i] })
      ] }, i)),
      "]"
    ] });
  }
  const msgsForPath = messages.filter((m) => pathMatches(m.path, basePath));
  return /* @__PURE__ */ jsxs("span", { children: [
    msgsForPath.length > 0 && /* @__PURE__ */ jsx("span", { className: messageClass, children: msgsForPath.map((m, i) => /* @__PURE__ */ jsx("span", { children: m.message }, i)) }),
    typeof data === "string" ? `"${data}"` : String(data)
  ] });
};
const Errors = reactive.component(
  ({ data }) => {
    const rows = data.rows ?? [];
    return /* @__PURE__ */ jsxs("div", { className: "flex min-h-0 flex-col gap-4 overflow-hidden", children: [
      /* @__PURE__ */ jsxs(Alert, { status: "danger", children: [
        /* @__PURE__ */ jsx(Alert.Indicator, {}),
        /* @__PURE__ */ jsxs(Alert.Content, { children: [
          /* @__PURE__ */ jsx(Alert.Title, { children: "Import Errors" }),
          /* @__PURE__ */ jsxs(Alert.Description, { children: [
            /* @__PURE__ */ jsx("strong", { children: data.errorsCount }),
            " errors found. A limited sample of errors is shown below. Please review and fix the errors and try your import again."
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsx(ScrollShadow, { className: "min-h-0 flex-1 overflow-y-auto overflow-x-hidden", children: /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-3 pr-2", children: rows.map((row) => /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsx(Card.Content, { className: "px-4 pt-2 pb-4", children: /* @__PURE__ */ jsx("div", { className: "font-mono text-sm", children: /* @__PURE__ */ jsx(DataTree, { data: row.data ?? {}, messages: row.messages ?? [] }) }) }) }, row.id)) }) })
    ] });
  },
  {
    name: "Errors",
    queries: () => [
      {
        name: "rows",
        execute: (db) => db.records.where("valid").equals(0).limit(10).toArray()
      },
      {
        name: "errorsCount",
        execute: (db) => db.records.where("valid").equals(0).count()
      }
    ]
  }
);
Errors.Modal = withModalController(({ onClose, ...props }) => /* @__PURE__ */ jsx(Errors, { onClose, ...props }));
const ReactiveImporter = withReactive(
  reactive.component(
    ({
      services,
      db,
      validator,
      dataHandler,
      onStart,
      onComplete,
      onClose,
      ref,
      ...rest
    }) => {
      rest = omit(rest, ["monitors", "data", "reactors"]);
      const [{ open }, ErrorsModal] = useModalController(Errors.Modal, {
        title: "",
        scrollBehavior: "inside"
      });
      const handleFetchRecords = useCallback(
        async (offset = 0, limit = 1e3) => {
          const results = await reactive.database.records.offset(offset).limit(limit).toArray();
          return results.map((x) => x.data);
        },
        [reactive]
      );
      const handleProcessRecords = useCallback(
        async (handler, options) => {
          options = { mode: "batch", batchSize: 1e3, ...options };
          if (options.mode === "all") {
            const results = await reactive.database.records.toArray();
            await handler(results.map((x) => x.data));
          } else if (options.mode === "single") {
            await reactive.database.records.each(
              (x) => handler(x.data)
            );
          } else if (options.mode === "batch") {
            let offset = 0;
            let batch = await handleFetchRecords(offset, options.batchSize);
            while (batch.length > 0) {
              await handler(batch);
              offset += options.batchSize;
              batch = await handleFetchRecords(offset, options.batchSize);
            }
          }
        },
        [reactive]
      );
      useImperativeHandle(ref, () => ({
        process: handleProcessRecords,
        fetch: handleFetchRecords
      }));
      const handleOnStart = (data) => {
        services.import.clear();
        onStart?.(data);
      };
      const handleDataHandler = async (rows, info) => {
        await services.import.save(rows);
        await Promise.resolve(dataHandler(rows, info));
      };
      const handleOnComplete = async (data) => {
        const errors = await db.records.where("valid").equals(0).count();
        if (errors > 0) {
          open({});
        } else {
          data.records = await db.records.count();
          return await Promise.resolve(onComplete?.(data));
        }
      };
      const handleOnClose = async (data) => {
        const errors = await db.records.where("valid").equals(0).count();
        if (errors === 0) {
          return await Promise.resolve(onClose?.(data));
        }
      };
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          CsvImporter,
          {
            validator,
            dataHandler: handleDataHandler,
            onStart: handleOnStart,
            onComplete: handleOnComplete,
            onClose: onClose && handleOnClose,
            ...rest
          }
        ),
        /* @__PURE__ */ jsx(ErrorsModal, {})
      ] });
    },
    {
      name: "ReactiveImporter"
    }
  )
);
export {
  CsvImporter,
  ImporterField,
  ReactiveImporter
};
//# sourceMappingURL=index.js.map
